<template>
	<div :class="{'border border-green border-md pa-2': debugMode}">
		<template v-if="debugMode">
			<div class="ao-p3-regular">MainOfDateRangePicker.vue</div>
			<pre
				class="mb-2"
				style="padding: 0.5rem; background-color: #3c3b3b; color: rgb(39, 136, 18); font-size: 12px;"
			><code>{{ {
				modelValue,
				range,
				rangeAsArray,
				dateFrom,
				dateTo,
				timeFrom,
				timeTo,
				min,
				max,
				startDay,
				hasBeenChangedTimeManually,
			} }}</code></pre>
		</template>
		<v-menu
			v-model="isShowMenu"
			:close-on-content-click="false"
			min-width="278"
			@update:modelValue="onUpdateIsShowMenuStatus"
		>
			<template #activator="{ props }">
				<ActivatorOfDateRangePicker
					v-model="isShowMenu"
					v-bind="props"
					:label="label"
					:placeholder="placeholder"
					:error="error"
					:errorMessages="errorMessages"
					:text="textOfActivator"
					:density="density"
					:hideDetails="hideDetails"
				/>
			</template>
			<v-card
				class="ao-great-main-date-range-picker"
				style="overflow: unset;"
			>
				<div class="pa-2">
					<LayoutOfDateRangePicker
						:calendarHeight="calendarHeight"
						:showRangesList="rangesList.length >= 1"
					>

						<template #date-from>
							<DateSelectorOfDateRange
								v-model="dateFrom"
								:min="min"
								:max="dateTo"
								:debugMode="debugMode"
							/>
						</template>

						<template #time-from>
							<TimeSelectorOfDateRange
								v-model="timeFrom"
								:debugMode="debugMode"
							/>
						</template>

						<template #date-to>
							<DateSelectorOfDateRange
								v-model="dateTo"
								:min="dateFrom"
								:max="max"
								:debugMode="debugMode"
							/>
						</template>

						<template #time-to>
							<TimeSelectorOfDateRange
								v-model="timeTo"
								:debugMode="debugMode"
							/>
						</template>

						<template #settings>
							<DateRangeSettingsMenu
								@openStartDayDialog="isActiveStartDayDialog = true"
							/>
							<v-dialog
								:modelValue="isActiveStartDayDialog"
								:width="400"
								persistent
							>
								<DateRangeStartDayForm
									@cancel="isActiveStartDayDialog = false"
								/>
							</v-dialog>
						</template>

						<template #ranges>
							<v-list>
								<v-list-item
									v-for="item in rangesList"
									:key="`predefinedRanges-${item.key}`"
									height="30"
									max-height="30"
									min-height="30"
									:value="item.title"
									:active="item.key === range"
									@click="updateRange(item.key)"
								>
									{{ item.title }}
								</v-list-item>
							</v-list>
						</template>

						<template #calendar>
							<VueDatePicker
								:id="componentId"
								ref="DatePickerComponent"
								:modelValue="rangeAsArray"
								range
								auto-apply
								:enable-time-picker="false"
								:min-date="min"
								:max-date="max"
								model-type="yyyy-MM-dd HH:mm:ss"
								inline
								month-name-format="short"
								:locale="$i18n.locale"
								calendar-class-name="ao-great-date-range-picker-calendar"
								menu-class-name="ao-great-date-range-picker-menu"
								class="ao-great-date-range-picker"
								@update:modelValue="onUpdateRange"
							/>
						</template>

						<template #footer>
							<div class="d-flex">
								<ao-btn
									class="flex-grow-1"
									color="primary"
									variant="flat"
									@click="onClickApply"
								>
									{{ $t('Apply') }}
								</ao-btn>

								<ao-btn
									class="flex-grow-1"
									variant="text"
									@click="onClickCancel"
								>
									{{ $t('Cancel') }}
								</ao-btn>

								<!-- <ao-btn
									class="flex-grow-1"
									variant="text"
									@click="handleClearRanges"
								>
									Очистить даты
								</ao-btn> -->
							</div>
						</template>

					</LayoutOfDateRangePicker>
				</div>
			</v-card>
		</v-menu>
	</div>
</template>

<script setup>
import { ref, computed } from 'vue'

import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'

import useMoment from '@/composables/useMoment.js'

import ActivatorOfDateRangePicker from './ActivatorOfDateRangePicker.vue'
import LayoutOfDateRangePicker from './LayoutOfDateRangePicker.vue'
import DateSelectorOfDateRange from './DateSelectorOfDateRange.vue'
import TimeSelectorOfDateRange from './TimeSelectorOfDateRange.vue'
import DateRangeSettingsMenu from './DateRangeSettingsMenu.vue'
import DateRangeStartDayForm from './DateRangeStartDayForm.vue'

const props = defineProps({

	modelValue: {
		type: [Array, String],
		default: () => (null),
	},

	ranges: {
		type: Array,
		default: () => (null),
	},

	min: {
		type: String,
	},

	max: {
		type: String,
	},

	startDay: {
		type: Number,
	},

	label: {
		type: String,
	},

	placeholder: {
		type: String,
	},

	error: {
		type: Boolean,
		default: false,
	},

	errorMessages: {
		type: [Array, String],
		default: () => ([]),
	},

	density: {
		type: String,
		default: 'default',
	},

	debugMode: {
		type: Boolean,
		default: false,
	},

	hideDetails: {
		type: [Boolean, String],
		default: false,
	},

	variant: {
		type: String,
		default: 'input',
		validator: (value) => ['input', 'button'].includes(value),
	},

})

const emit = defineEmits([
	'update:modelValue',
])

const {
	moment,
	predefinedRanges,
	predefinedRangesArray,
	formatDateTime,
	updateDateTimeString,
} = useMoment()

const DatePickerComponent = ref(null)
const componentId = 'great-date-range-picker' + Math.random().toString(36).split('.')[1]
const calendarHeight = ref(null)

const isShowMenu = ref(false)

const isActiveStartDayDialog = ref(false)

/* этот флаг нужен для того, что бы если пользователь вручную выбрал время, то оно не сбивалось при смене даты */
const hasBeenChangedTimeManually = ref(false)

const onUpdateIsShowMenuStatus = (value) => {
	// debugger
	if (props.debugMode) {
		// eslint-disable-next-line no-console
		console.log('onUpdateIsShowMenuStatus', value, isShowMenu.value)
	}
	if (value) {
		setRangeFromProps()

		setHightValueForRanges()
	}
	hasBeenChangedTimeManually.value = false
}

const getDateStringFromRange = (index) => {
	if (!range.value) return null
	return formatDateTime(rangeAsArray.value[index], 'YYYY-MM-DD')
}

const getTimeObjectFromRange = (index) => {
	if (!range.value) return null
	const time = {
		hours: 0,
		minutes: 0,
	}
	let dateTime = rangeAsArray.value[index]
	if (dateTime) {
		time.hours = formatDateTime(dateTime, 'H')
		time.minutes = formatDateTime(dateTime, 'm')
	}
	return time
}

const getUpdatedDateTimeFromRangeByNewValues = (index, value) => {
	if (index === null) {
		return updateDateTimeString(moment().format(), value)
	}
	if (!range.value) return null
	return updateDateTimeString(rangeAsArray.value[index], value)
}

const range = ref([])
const rangeAsArray = computed(() => {
	if (!range.value) return null
	if (typeof range.value === 'string') {
		// debugger
		return predefinedRanges.value[range.value].getRange().map((date) => (formatDateTime(date, 'YYYY-MM-DD HH:mm:ss')))
	}
	if (Array.isArray(range.value)) {
		// debugger
		return range.value.map((date) => (formatDateTime(date, 'YYYY-MM-DD HH:mm:ss')))
	}
	return range.value
})

const dateFrom = computed({
	get() {
		return getDateStringFromRange(0)
	},
	set(value) {
		if (props.debugMode) {
			// eslint-disable-next-line no-console
			console.log('dateFrom', value)
		}
		const payload = {
			year: formatDateTime(value, 'YYYY'),
			month: formatDateTime(value, 'MM') - 1,
			dates: formatDateTime(value, 'D'),
		}
		if (rangeAsArray.value === null) {
			const newDateFrom = getUpdatedDateTimeFromRangeByNewValues(null, payload)
			const newDateTo = getUpdatedDateTimeFromRangeByNewValues(null, payload)
			updateRange([newDateFrom, newDateTo])
			return
		}
		const newDateFrom = getUpdatedDateTimeFromRangeByNewValues(0, payload)
		updateRange([newDateFrom, rangeAsArray.value[1]])
	},
})

const dateTo = computed({
	get() {
		return getDateStringFromRange(1)
	},
	set(value) {
		if (props.debugMode) {
			// eslint-disable-next-line no-console
			console.log('dateTo', value)
		}
		const payload = {
			year: formatDateTime(value, 'YYYY') * 1,
			month: formatDateTime(value, 'MM') - 1,
			dates: formatDateTime(value, 'D') * 1,
		}
		if (rangeAsArray.value === null) {
			const newDateFrom = getUpdatedDateTimeFromRangeByNewValues(null, payload)
			const newDateTo = getUpdatedDateTimeFromRangeByNewValues(null, payload)
			updateRange([newDateFrom, newDateTo])
			return
		}
		const newDateTo = getUpdatedDateTimeFromRangeByNewValues(1, payload)
		updateRange([rangeAsArray.value[0], newDateTo])
	},
})

const timeFrom = computed({
	get() {
		return getTimeObjectFromRange(0)
	},
	set(value) {
		if (props.debugMode) {
			// eslint-disable-next-line no-console
			console.log('timeFrom', value)
		}
		const newTimeFrom = getUpdatedDateTimeFromRangeByNewValues(0, {
			...value,
			seconds: 0,
		})
		updateRange([newTimeFrom, rangeAsArray.value[1]])
		hasBeenChangedTimeManually.value = true
	},
})

const timeTo = computed({
	get() {
		return getTimeObjectFromRange(1)
	},
	set(value) {
		if (props.debugMode) {
			// eslint-disable-next-line no-console
			console.log('timeTo', value)
		}
		const newTimeTo = getUpdatedDateTimeFromRangeByNewValues(1, {
			...value,
			seconds: 59,
		})
		updateRange([rangeAsArray.value[0], newTimeTo])
		hasBeenChangedTimeManually.value = true
	},
})

const updateRange = (value) => {
	if (props.debugMode) {
		// eslint-disable-next-line no-console
		console.log('updateRange', value)
	}
	if (!value) {
		range.value = null
		return
	}
	if (typeof value === 'string') {
		range.value = value
		// debugger
		return
	}
	// debugger
	range.value = [formatDateTime(value[0], 'YYYY-MM-DD HH:mm:ss'), updateDateTimeString(value[1], { seconds: 59 }, 'YYYY-MM-DD HH:mm:ss')]
}

const setRangeFromProps = () => {
	if (props.debugMode) {
		// eslint-disable-next-line no-console
		console.log('setRangeFromProps', props.modelValue)
	}
	updateRange(props.modelValue)
}

const onUpdateRange = (value) => {
	// debugger
	if (props.debugMode) {
		// eslint-disable-next-line no-console
		console.log('onUpdateRange', value)
	}
	if (!hasBeenChangedTimeManually.value && Array.isArray(value) && value[0] && value[1]) {
		if (props.debugMode) {
			// eslint-disable-next-line no-console
			console.log('startDay', props.startDay)
		}
		let startTimeOfDay = '00:00:00'
		let endTimeOfDay = '23:59:59'
		if (props.startDay && (formatDateTime(value[0], 'YYYY-MM-DD') !== formatDateTime(value[1], 'YYYY-MM-DD'))) {
			startTimeOfDay = moment.unix(props.startDay).utc().format('HH:mm:ss')
			endTimeOfDay = moment.unix(props.startDay).utc().add(1, 'day').subtract(1, 'second').format('HH:mm:ss')
		}
		const fullDayRange = [
			formatDateTime(value[0], 'YYYY-MM-DD') + ' ' + startTimeOfDay,
			formatDateTime(value[1], 'YYYY-MM-DD') + ' ' + endTimeOfDay,
		]
		updateRange(fullDayRange)
		return
	}
	updateRange(value)
	// emit('update:modelValue', value)
}

const onClickApply = () => {
	if (props.debugMode) {
		// eslint-disable-next-line no-console
		console.log('onClickApply')
	}
	emit('update:modelValue', range.value)
	isShowMenu.value = false
}

const onClickCancel = () => {
	if (props.debugMode) {
		// eslint-disable-next-line no-console
		console.log('onClickCancel')
	}
	setRangeFromProps()
	isShowMenu.value = false
}

const rangesList = computed(() => {
	if (props.ranges === null) return predefinedRangesArray.value
	if (Array.isArray(props.ranges)) return props.ranges
	return []
})

const textOfActivator = computed(() => {
	if (!props.modelValue) {
		return null
	}

	const format = 'DD.MM.YYYY HH:mm'
	let from, to = null

	if (typeof props.modelValue === 'string') {
		const rangeFromPredefined = predefinedRanges.value[props.modelValue].getRange()
		from = formatDateTime(rangeFromPredefined[0], format)
		to = formatDateTime(rangeFromPredefined[1], format)
	}
	if (Array.isArray(props.modelValue)) {
		from = formatDateTime(props.modelValue[0], format)
		to = formatDateTime(props.modelValue[1], format)
	}
	return `${from} - ${to}`
})

const setHightValueForRanges = () => {
	setTimeout(() => {
		const element = document.querySelector(`#${componentId}`)
		calendarHeight.value = element?.offsetHeight ?? null
	}, 10)
}

</script>

<style lang="scss">
.ao-great-main-date-range-picker {
	font-size: 12px;

	.ao-great-date-picker,
	.ao-great-time-picker {
		width: auto;

		.dp__calendar {
			font-family: 'Inter', sans-serif;
			font-style: normal;
			font-weight: 500;
			font-size: 12px;
			line-height: 150%;
		}

	}

}

.ao-great-date-range-picker {

	.ao-great-date-range-picker-menu {
		border: none;
	}

	.dp__calendar {
		font-family: 'Inter', sans-serif;
		font-style: normal;
		font-weight: 500;
		font-size: 12px;
		line-height: 150%;
	}

}
</style>
